import {
  applyH, areasOf, artRectIn, clamp, insideUnit, quadBounds, quadToSquare, squareToQuad, titleCaseId,
  type MockupJob, type MockupTemplate, type Placement, type Point, type PrintArea, type Quad,
} from '@eip/engine';
import type { AppMode } from '../state/sep';
import type { DocEntry } from '../state/store';
import { OFFSET_IN_MAX, OFFSET_IN_MIN, WIDTH_IN_MAX, WIDTH_IN_MIN, type DocMockup, type TemplateStatus } from '../state/mockup';
import type { TemplateSource } from './templates';

/**
 * Where a photo pixel falls in the print area's own unit square, or null when
 * the quad is degenerate or the point is off the vanishing line (`applyH`
 * returns NaN there rather than throwing).
 *
 * Deliberately NOT range-checked: the drag below has to keep tracking a pointer
 * that has left the print area — the placement it produces is clamped by inches,
 * not by the quad — while `photoToInches` is the checked form the hit test uses.
 */
function unitAt(template: MockupTemplate, px: Point): Point | null {
  let uv: Point;
  try {
    uv = applyH(quadToSquare(template.printArea.quad), px[0], px[1]);
  } catch {
    return null;   // a degenerate quad: nothing on the canvas maps anywhere
  }
  return Number.isFinite(uv[0]) && Number.isFinite(uv[1]) ? uv : null;
}

/** The same mapping in inches, without the unit-square gate. */
function inchesAt(template: MockupTemplate, px: Point): Point | null {
  const uv = unitAt(template, px);
  if (!uv) return null;
  const area = template.printArea;
  return [uv[0] * area.widthIn, uv[1] * area.heightIn];
}

/**
 * A photo pixel as a position in the print area, in inches from its top-left —
 * the inverse of the homography the compositor samples the art through, so the
 * viewer's hit test and the render agree on where the area is to the pixel.
 *
 * Null OUTSIDE the area (which is what makes it a hit test: the viewer starts a
 * placement drag on a hit and pans on a miss), and null for a quad too
 * degenerate to invert.
 */
export function photoToInches(template: MockupTemplate, px: Point): Point | null {
  const uv = unitAt(template, px);
  if (!uv || !insideUnit(uv[0], uv[1])) return null;
  const area = template.printArea;
  return [uv[0] * area.widthIn, uv[1] * area.heightIn];
}

/** The art rectangle's four corners in photo pixels, clockwise from top-left —
 *  the transform box the viewer draws and the drag grabs. */
export type PrintBoxCorners = [Point, Point, Point, Point];
/** Which handle a press landed on, in the same order as `PrintBoxCorners`. */
export type PrintBoxCorner = 'corner:tl' | 'corner:tr' | 'corner:br' | 'corner:bl';
/** What a press inside the print area found: the rotate handle, a resize handle, the art itself,
 *  or nothing. */
export type PrintBoxHit = PrintBoxCorner | 'rotate' | 'move' | null;
/** What the pointer is over, for the canvas cursor. Beyond a `PrintBoxHit`:
 *  `'area'` is the print area AROUND the art (a press there moves it too) and
 *  `'dragging'` is a move gesture already in hand. */
export type CursorTarget = PrintBoxHit | 'area' | 'dragging';

/**
 * How far a plain drag must travel before it counts as one. A bare click — and
 * the double-click that refits the viewport — lands with the pointer exactly
 * where it started, and without this floor it would still rewrite `'center'`
 * as a number and cost a render for a gesture that moved nothing. A hundredth
 * of an inch is ~3 px on a 300 dpi blank: below anything a hand can aim at, and
 * far below the first pixel of a real drag.
 */
const DRAG_MIN_IN = 0.01;

/** The corner handles' HIT radius in SCREEN pixels, and the radius the dot is
 *  actually drawn at — a target comfortably larger than the mark, which is what
 *  makes a corner easy to grab without putting a blob over the art. Both are
 *  divided by the viewport scale wherever they are used, so neither changes size
 *  with the zoom. */
export const HANDLE_HIT_PX = 8;
export const HANDLE_DOT_PX = 5;
/** How far above the box's top edge the rotate handle sits, in SCREEN pixels — far enough to
 *  clear the two top corner handles at any zoom, close enough to read as part of the box. Divided
 *  by the viewport scale wherever it is used, like the two above. */
export const ROTATE_HANDLE_PX = 24;

/** The corners, clockwise from top-left, of the corner opposite each handle:
 *  what a corner drag scales about. */
const OPPOSITE: Record<PrintBoxCorner, 0 | 1 | 2 | 3> = { 'corner:tl': 2, 'corner:tr': 3, 'corner:br': 0, 'corner:bl': 1 };
const CORNER_INDEX: Record<PrintBoxCorner, 0 | 1 | 2 | 3> = { 'corner:tl': 0, 'corner:tr': 1, 'corner:br': 2, 'corner:bl': 3 };

/**
 * A point turned `deg` about `centre`, in the area's own (y-down) inch space — CLOCKWISE for a
 * positive angle, which is the compositor's convention: `renderMockup` samples the art through the
 * INVERSE turn, so the art the viewer draws goes the other way round. Everything rotation touches
 * here goes through this one function so the box, the scale and the render cannot disagree.
 */
export function rotatePointIn(p: Point, centre: Point, deg: number): Point {
  if (!deg) return p;
  const th = (deg * Math.PI) / 180, cos = Math.cos(th), sin = Math.sin(th);
  const dx = p[0] - centre[0], dy = p[1] - centre[1];
  return [centre[0] + dx * cos - dy * sin, centre[1] + dx * sin + dy * cos];
}

/**
 * The placed art's four corners in PHOTO pixels — `artRectIn`'s inch rectangle, turned
 * `rotationDeg` about its own centre, mapped through the AREA's own homography, so a keystoned
 * quad gives a keystoned box rather than the axis-aligned rectangle its bounding box would.
 *
 * Deliberately NOT clipped to the quad: art placed (or dragged) past the print
 * area's edge is drawn where it actually is, with the render's own clipping the
 * only thing that hides it — the same reasoning as `dragToPlacement`'s.
 *
 * Null for a quad too degenerate to build a homography from, which is the one
 * case `squareToQuad` throws on: the viewer then draws no box rather than
 * failing the whole canvas.
 */
export function areaBoxCorners(area: PrintArea, placement: Placement, art: { width: number; height: number }, rotationDeg = 0): PrintBoxCorners | null {
  let H;
  try {
    H = squareToQuad(area.quad);
  } catch {
    return null;
  }
  const r = artRectIn(area, placement, art);
  const centre: Point = [r.x0 + r.w / 2, r.y0 + r.h / 2];
  const at = (xIn: number, yIn: number): Point => {
    const [x, y] = rotatePointIn([xIn, yIn], centre, rotationDeg);
    return applyH(H, x / area.widthIn, y / area.heightIn);
  };
  const c: PrintBoxCorners = [at(r.x0, r.y0), at(r.x0 + r.w, r.y0), at(r.x0 + r.w, r.y0 + r.h), at(r.x0, r.y0 + r.h)];
  return c.every((p) => Number.isFinite(p[0]) && Number.isFinite(p[1])) ? c : null;
}

/** The same, for the job's PRIMARY print — the box the viewer's drag grabs. */
export function printBoxCorners(template: MockupTemplate, placement: Placement, art: { width: number; height: number }, rotationDeg = 0): PrintBoxCorners | null {
  return areaBoxCorners(template.printArea, placement, art, rotationDeg);
}

/**
 * Where the rotate handle sits: `offsetPx` (photo pixels — the caller divides the screen constant
 * by the viewport scale) beyond the middle of the box's TOP edge, along the box's own "up", so it
 * turns with the box instead of hanging off the screen's vertical.
 *
 * Null when the box has no height to take a direction from (art of zero height, a degenerate
 * quad): there is nowhere to put the handle, and the viewer simply draws none.
 */
export function rotateHandleAt(corners: PrintBoxCorners, offsetPx: number): Point | null {
  const [tl, tr, br, bl] = corners;
  const mx = (tl[0] + tr[0]) / 2, my = (tl[1] + tr[1]) / 2;
  const bx = (bl[0] + br[0]) / 2, by = (bl[1] + br[1]) / 2;
  const dx = mx - bx, dy = my - by;
  const len = Math.hypot(dx, dy);
  if (!(len > 1e-9) || !Number.isFinite(len)) return null;
  return [mx + (dx / len) * offsetPx, my + (dy / len) * offsetPx];
}

/** Every printable area on a blank, for the viewer's outlines: the primary one first and flagged
 *  (it is the one the transform box belongs to), each with the name the panel and the inspector
 *  call it by. An area with an id but no name is title-cased from the id, exactly as the engine
 *  names one; a pre-3C area with neither says nothing rather than inventing a name. */
export function areaOutlines(template: MockupTemplate): Array<{ id: string; name: string; quad: Quad; primary: boolean }> {
  return areasOf(template).map((a, i) => ({
    id: a.id ?? '',
    name: a.name ?? (a.id ? titleCaseId(a.id) : ''),
    quad: a.quad,
    primary: i === 0,
  }));
}

/** Ray casting rather than a convexity test: the art box is convex for a sane
 *  quad, but a press must still answer sensibly for a keystone extreme enough
 *  to fold it. */
function insidePolygon(p: Point, poly: readonly Point[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > p[1]) !== (yj > p[1]) && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/**
 * What a press at `px` (photo pixels) grabs: the rotate handle (when the caller offers one —
 * `rotateHandleAt`), else a corner handle within `handleRadiusPx`, else the art itself, else
 * nothing.
 *
 * The rotate handle is tested BEFORE the corners because on small art it overlaps them: it is the
 * one the user aimed at, since a corner is reachable everywhere else along the box's edge.
 *
 * Handles are tested before the MOVE region, and against the nearest one, because they sit ON the
 * box's edge — half of every handle is inside the move region, and testing the
 * region first would make the resize unreachable on small art.
 */
export function hitPrintBox(px: Point, corners: PrintBoxCorners, handleRadiusPx: number, rotateAt?: Point | null): PrintBoxHit {
  if (rotateAt && (px[0] - rotateAt[0]) ** 2 + (px[1] - rotateAt[1]) ** 2 <= handleRadiusPx * handleRadiusPx) return 'rotate';
  let best: PrintBoxCorner | null = null, bestD = handleRadiusPx * handleRadiusPx;
  for (const corner of ['corner:tl', 'corner:tr', 'corner:br', 'corner:bl'] as const) {
    const c = corners[CORNER_INDEX[corner]];
    const d = (px[0] - c[0]) ** 2 + (px[1] - c[1]) ** 2;
    if (d <= bestD) { best = corner; bestD = d; }
  }
  if (best) return best;
  return insidePolygon(px, corners) ? 'move' : null;
}

/**
 * The placement a corner drag produces: a UNIFORM scale (the art's aspect ratio
 * is fixed by its pixels, exactly as `artRectIn` fixes it) about the OPPOSITE
 * corner, which therefore stays exactly where it was.
 *
 * The new width is the old one times how much further along the box's DIAGONAL
 * the pointer has travelled — projecting onto the diagonal rather than taking a
 * distance ratio is what keeps a drag that wanders off the diagonal from
 * spinning the scale around, and what makes dragging straight back past the
 * anchor shrink to the floor instead of growing again.
 *
 * Measured from the placement the gesture STARTED at, like `dragToPlacement`,
 * so a throttled drag lands where an unthrottled one would.
 *
 * `shift` scales about the art's CENTRE instead, and — as in `dragToPlacement`
 * — a centred axis stays `'center'` (on either axis, independently), since
 * holding the centre is what that means.
 *
 * A press that never really moved is not a scale at all: it returns `start`
 * unchanged, on the same `DRAG_MIN_IN` floor the move gesture uses, so a click
 * on a handle (or the double-click that refits the viewport) cannot rewrite
 * `'center'` as a number and cost a render for a gesture that moved nothing.
 */
export function dragToScale(
  start: Placement,
  corner: PrintBoxCorner,
  from: Point,
  to: Point,
  template: MockupTemplate,
  art: { width: number; height: number },
  shift = false,
  rotationDeg = 0,
): Placement {
  const a0 = inchesAt(template, from), b0 = inchesAt(template, to);
  if (!a0 || !b0) return start;
  // A click, not a drag — see `DRAG_MIN_IN`. Measured before the un-turn below, which is a
  // rotation about a point and so cannot change the distance anyway.
  if (Math.hypot(b0[0] - a0[0], b0[1] - a0[1]) < DRAG_MIN_IN) return start;
  const area = template.printArea;
  const rect = artRectIn(area, start, art);
  const box: Point[] = [[rect.x0, rect.y0], [rect.x0 + rect.w, rect.y0], [rect.x0 + rect.w, rect.y0 + rect.h], [rect.x0, rect.y0 + rect.h]];
  const grabbed = box[CORNER_INDEX[corner]];
  const cx = rect.x0 + rect.w / 2, cy = rect.y0 + rect.h / 2;
  // A turned box is scaled in its OWN frame: un-turn the pointer about the art's centre and every
  // line below is the square-on arithmetic this always did — which is also why a 0° drag is
  // unchanged to the last bit (`rotatePointIn` returns the point itself).
  const a = rotatePointIn(a0, [cx, cy], -rotationDeg), b = rotatePointIn(b0, [cx, cy], -rotationDeg);
  const anchor: Point = shift ? [cx, cy] : box[OPPOSITE[corner]];
  const dx = grabbed[0] - anchor[0], dy = grabbed[1] - anchor[1];
  const from0 = (a[0] - anchor[0]) * dx + (a[1] - anchor[1]) * dy;
  // The press started on the anchor (or on a zero-size box): there is no
  // projection to take a ratio of, and any answer would be arbitrary.
  if (Math.abs(from0) < 1e-9) return start;
  const ratio = ((b[0] - anchor[0]) * dx + (b[1] - anchor[1]) * dy) / from0;
  if (!Number.isFinite(ratio)) return start;
  // The same ceiling as the Width field (`WIDTH_IN_MAX`), not the print area's width: art wider
  // than the area is a legitimate (clipped, warned-about) placement, and capping the drag at the
  // area meant a print that STARTED wider — a 17 in native design in a 12 in area — could be
  // shrunk into the box by hand but never dragged back out of it (hands-on, 2026-09-27).
  const widthIn = clamp(start.widthIn * ratio, WIDTH_IN_MIN, WIDTH_IN_MAX);
  const heightIn = art.width > 0 ? (widthIn * art.height) / art.width : 0;
  if (shift) {
    return {
      widthIn,
      xIn: start.xIn === 'center' ? 'center' : clamp(cx - widthIn / 2, OFFSET_IN_MIN, OFFSET_IN_MAX),
      // The same for the vertical centre, and for the same reason: art centred DOWN the area and
      // scaled about its own centre is still centred down it, and writing the number here would
      // lose the "keep this centred" intent the next width change depends on.
      yIn: start.yIn === 'center' ? 'center' : clamp(cy - heightIn / 2, OFFSET_IN_MIN, OFFSET_IN_MAX),
    };
  }
  // The anchor corner is held: NEITHER axis stays `'center'`, because the art is
  // now pinned by a corner the user is holding, not by the area's midlines.
  const right = corner === 'corner:tl' || corner === 'corner:bl';
  const bottom = corner === 'corner:tl' || corner === 'corner:tr';
  const x0 = anchor[0] - (right ? widthIn : 0), y0 = anchor[1] - (bottom ? heightIn : 0);
  // Holding the anchor square-on is not the same as holding it on SCREEN once the art is turned:
  // the rect turns about its own centre, and the scale has just moved that centre. Shifting the
  // result by how far the turned anchor slipped pins the corner the user is actually holding.
  // A no-op at 0°, where the two anchors are the same point.
  const held = rotationDeg ? rotatePointIn(anchor, [cx, cy], rotationDeg) : anchor;
  const moved = rotationDeg ? rotatePointIn(anchor, [x0 + widthIn / 2, y0 + heightIn / 2], rotationDeg) : anchor;
  return {
    widthIn,
    xIn: clamp(x0 + held[0] - moved[0], OFFSET_IN_MIN, OFFSET_IN_MAX),
    yIn: clamp(y0 + held[1] - moved[1], OFFSET_IN_MIN, OFFSET_IN_MAX),
  };
}

/**
 * The angle a rotate-handle drag produces: the placement's angle plus the bearing the pointer has
 * swept about the art's centre, both in the area's own (y-down) inch space, so a clockwise drag is
 * a positive turn exactly as the compositor reads one.
 *
 * Measured from the angle the gesture STARTED at, like `dragToPlacement` and `dragToScale`, so a
 * throttled drag lands where an unthrottled one would.
 *
 * Snapped to whole degrees — a mockup turned 12.43° is noise, and the panel's field types whole
 * ones — or to 15° steps with `shift`, which is what makes 90° reachable by hand. The result is
 * normalised to (−180, 180], the range `sanitizeJob` holds a job to, so a gesture that goes round
 * and round cannot wind the angle up past what could have been typed.
 *
 * A press that never really moved leaves the angle alone: the bearing change is zero, the snap
 * returns the angle it was given, and the commit that ends the gesture writes nothing.
 */
export function dragToRotation(start: number, centreIn: Point, from: Point, to: Point, shift = false): number {
  const bearing = (p: Point) => Math.atan2(p[1] - centreIn[1], p[0] - centreIn[0]);
  const a = from[0] - centreIn[0], b = from[1] - centreIn[1];
  // The press landed on the centre itself: there is no bearing to measure a change from, and any
  // answer would be arbitrary — the same guard `dragToPlacement` keeps for a shift-scale.
  if (Math.hypot(a, b) < 1e-9) return start;
  const delta = ((bearing(to) - bearing(from)) * 180) / Math.PI;
  const step = shift ? 15 : 1;
  return normalizeDeg(Math.round((start + delta) / step) * step);
}

/**
 * `dragToRotation` for a gesture in PHOTO pixels — what the viewer's pointer actually gives it.
 * Both ends are mapped into the print area's own inch space through the UNGATED mapping
 * `dragToScale` uses, so a pointer dragged clean off the garment keeps turning the art, and the
 * centre is the art rect's at the placement the gesture started from.
 */
export function dragToRotationAt(
  start: number,
  startPlacement: Placement,
  from: Point,
  to: Point,
  template: MockupTemplate,
  art: { width: number; height: number },
  shift = false,
): number {
  const a = inchesAt(template, from), b = inchesAt(template, to);
  if (!a || !b) return start;
  const r = artRectIn(template.printArea, startPlacement, art);
  return dragToRotation(start, [r.x0 + r.w / 2, r.y0 + r.h / 2], a, b, shift);
}

/**
 * The turn a key press asks for on the mockup canvas: −1 for `[`, +1 for `]`, 0 for
 * anything else.
 *
 * `code` first, because `key` carries the SHIFTED character: a real browser reports
 * Shift+[ as `'{'`, so the 15° step the canvas's aria-label promises fell through to
 * nothing when only `'['` was matched. `code` is the physical key and is the same
 * either way; the characters stay as the fallback for a layout that reports neither
 * bracket code (and for a synthetic event carrying no `code` at all).
 *
 * The modifier rules are the whole reason this is a function. A bracket reached
 * through AltGr — the real `[` on German (AltGr+8), French and Nordic layouts — is
 * reported with `altKey`, and on Windows with `ctrlKey` as well, so rejecting every
 * modified press made the documented key unreachable on those layouts while the
 * physical bracket POSITION (`ü`, `^`) turned the print instead. Ctrl+[ on its own is
 * the browser's Back, and Meta never composes a character, so both are left alone.
 */
export function bracketTurn(e: { key: string; code?: string; ctrlKey?: boolean; metaKey?: boolean; altKey?: boolean }): -1 | 0 | 1 {
  const dir: -1 | 0 | 1 = e.code === 'BracketLeft' ? -1
    : e.code === 'BracketRight' ? 1
    : e.key === '[' || e.key === '{' ? -1
    : e.key === ']' || e.key === '}' ? 1
    : 0;
  if (!dir) return 0;
  if (e.metaKey) return 0;
  if (e.ctrlKey && !e.altKey) return 0;
  return dir;
}

/** An angle in (−180, 180] — 180 itself stays 180, and −180 comes back as its twin. */
export function normalizeDeg(deg: number): number {
  if (!Number.isFinite(deg)) return 0;
  return 180 - (((180 - deg) % 360) + 360) % 360;
}

/** The canvas cursor for what the pointer is over. */
export function cursorFor(hit: CursorTarget): string {
  switch (hit) {
    case 'corner:tl': case 'corner:br': return 'nwse-resize';
    case 'corner:tr': case 'corner:bl': return 'nesw-resize';
    case 'move': return 'move';
    case 'dragging': return 'grabbing';
    // The rotate handle and the bare print area are both "press here and drag": `grab`, which
    // becomes `grabbing` (`'dragging'`) for as long as the gesture lasts.
    case 'rotate': case 'area': return 'grab';
    default: return 'default';
  }
}

/** Two placements the drag would treat as the same write. */
export function samePlacement(a: Placement, b: Placement): boolean {
  return a.widthIn === b.widthIn && a.xIn === b.xIn && a.yIn === b.yIn;
}

/**
 * The placement a drag from `from` to `to` (both photo px) produces, always
 * measured from the placement the drag STARTED at — never from the last frame's
 * result — so a throttled drag lands exactly where an unthrottled one would and
 * no rounding accumulates over a gesture.
 *
 * Plain: the art moves by the drag's inch delta, and an axis stops being
 * `'center'` only once THAT axis has actually moved `DRAG_MIN_IN` — so a click
 * cannot un-centre anything, and a drag straight down leaves a centred `xIn`
 * centred because the user never touched it. Shift: `widthIn` is scaled
 * by how much further from the art's centre the pointer has moved, and the
 * centre is held still — which for a centred axis means `'center'` is KEPT, on
 * each axis independently, since staying centred is exactly what holding that
 * centre means.
 *
 * Both are held to the panel's own ranges, so a drag can never reach a
 * placement that could not have been typed.
 */
export function dragToPlacement(
  start: Placement,
  from: Point,
  to: Point,
  template: MockupTemplate,
  art: { width: number; height: number },
  shift: boolean,
): Placement {
  const a = inchesAt(template, from), b = inchesAt(template, to);
  if (!a || !b) return start;
  const rect = artRectIn(template.printArea, start, art);
  if (!shift) {
    const dx = b[0] - a[0], dy = b[1] - a[1];
    // The pointer has not really moved: this is a click (or a double-click on
    // the art to refit), not a placement.
    if (Math.hypot(dx, dy) < DRAG_MIN_IN) return start;
    // Then the SAME floor again, one axis at a time. A drag straight down is a
    // gesture about y and says nothing about x: writing `rect.x0` back as a
    // number there would quietly swap "keep this centred" for the inch it sits
    // at today, and the next width change would leave the art off-centre —
    // audit #5's whole complaint, reintroduced by a sideways-free drag. An axis
    // under the floor keeps whatever it had, `'center'` or a number, which is
    // the rule `nudgePlacement` holds an arrow key to as well.
    return {
      widthIn: start.widthIn,
      xIn: Math.abs(dx) >= DRAG_MIN_IN ? clamp(rect.x0 + dx, OFFSET_IN_MIN, OFFSET_IN_MAX) : start.xIn,
      yIn: Math.abs(dy) >= DRAG_MIN_IN ? clamp(rect.y0 + dy, OFFSET_IN_MIN, OFFSET_IN_MAX) : start.yIn,
    };
  }
  const cx = rect.x0 + rect.w / 2, cy = rect.y0 + rect.h / 2;
  const d0 = Math.hypot(a[0] - cx, a[1] - cy);
  // Started on the centre: there is no distance to take a ratio of, and any
  // answer would be arbitrary — leave the art exactly as it was.
  if (d0 < 1e-6) return start;
  const widthIn = clamp((start.widthIn * Math.hypot(b[0] - cx, b[1] - cy)) / d0, WIDTH_IN_MIN, WIDTH_IN_MAX);
  const heightIn = art.width > 0 ? (widthIn * art.height) / art.width : 0;
  return {
    widthIn,
    // Centred art scaled about its own centre is still centred; recomputing
    // `xIn`/`yIn` numerically here would only re-derive `(areaW − w) / 2` (and
    // `(areaH − h) / 2`) and lose the "keep this centred" intent the next width
    // change depends on.
    xIn: start.xIn === 'center' ? 'center' : clamp(cx - widthIn / 2, OFFSET_IN_MIN, OFFSET_IN_MAX),
    yIn: start.yIn === 'center' ? 'center' : clamp(cy - heightIn / 2, OFFSET_IN_MIN, OFFSET_IN_MAX),
  };
}

/**
 * The job a key press leaves behind: the print moved `dx`/`dy` INCHES and turned
 * `deg` degrees (audit #12 — the canvas was mouse-only, so a shop that cannot
 * hold a steady drag could not place art at all).
 *
 * The same rules as a drag, and for the same reason — the two write to one field
 * and must not disagree about what is legal:
 *  - a moved axis is UN-CENTRED first, at the inch position it was centred at, so
 *    the art carries on from where it is rather than jumping to 0;
 *  - the axis nobody moved keeps its `'center'` intent, so nudging across does
 *    not quietly pin the vertical centre the next width change would have kept;
 *  - offsets are clamped to `OFFSET_IN_MIN…OFFSET_IN_MAX` and the angle is
 *    normalised to (−180, 180], exactly as `dragToPlacement`/`dragToRotation` do.
 *
 * Returns the job it was GIVEN when nothing would move (every key at the clamp,
 * or a call with no deltas at all), so the commit that follows writes nothing and
 * a held-down arrow at the edge costs neither a render nor an undo entry.
 */
/** One key press along an axis: clamped like a drag, then rounded to a thousandth
 *  of an inch. A press is a DISCRETE step, and binary arithmetic on tenths is not
 *  exact — without this, two presses left `0.10000000000000009` in the panel's
 *  offset field for the shop to read. Far finer than any blank's own tolerance. */
function nudgedIn(from: number, delta: number): number {
  return Math.round(clamp(from + delta, OFFSET_IN_MIN, OFFSET_IN_MAX) * 1000) / 1000;
}

export function nudgePlacement(
  job: MockupJob,
  dx: number,
  dy: number,
  deg: number,
  ctx: { template: MockupTemplate; art: { width: number; height: number } },
): MockupJob {
  const rotationDeg = deg ? normalizeDeg((job.rotationDeg ?? 0) + deg) : job.rotationDeg ?? 0;
  const p = job.placement;
  let placement = p;
  if (dx || dy) {
    // Read through the same helper the render and the box use, so "where it is
    // now" is the same inch for a centred axis as the pixels on screen show.
    const rect = artRectIn(ctx.template.printArea, p, ctx.art);
    placement = {
      widthIn: p.widthIn,
      xIn: dx ? nudgedIn(rect.x0, dx) : p.xIn,
      yIn: dy ? nudgedIn(rect.y0, dy) : p.yIn,
    };
  }
  if (samePlacement(placement, p) && rotationDeg === (job.rotationDeg ?? 0)) return job;
  return { ...job, placement, rotationDeg };
}

/**
 * Is the mockup what the viewer is responsible for — rather than the DTF
 * preview or a separation? True as soon as the document has a job in mockup
 * mode, BEFORE the first render has landed: the bitmap can be a second away,
 * and the viewer says "rendering…" over a dimmed DTF preview in the meantime
 * rather than handing the canvas back to a mode the user has left.
 *
 * Exported so the inspector can gate its "Mockup" section on exactly the same
 * condition, the way `sepViewShown` serves the separation rows.
 */
export function mockupViewShown(appMode: AppMode, doc: DocEntry | undefined): boolean {
  return appMode === 'mockup' && !!doc?.mockup;
}

/**
 * Is there a second view to switch to? Three things have to hold, and each one
 * is a different way the pair can be half-built:
 *  - a back design is bound and has a mockup of its own (`backDoc`), which is
 *    what the Back view composites the art of;
 *  - the session has derived the pair's `backJob` — absent when the front
 *    blank's style has no back blank at all, which is the usual case today;
 *  - that back BLANK is prepared in the worker, since it is what the render
 *    goes onto. Its status, not the back document's own template's: the back
 *    document is previewed on a front blank in its own panel, and reading that
 *    one is what let the toggle show the back design on a FRONT blank.
 *
 * Nothing is written back when it goes false — `useMockupView` falls to the
 * front view where it reads this, as `useSepView` does for a removed plate.
 */
export function canToggleMockupView(
  mockup: DocMockup | undefined,
  backDoc: DocEntry | undefined,
  templates: TemplateSource[],
  templateStatus: Record<string, TemplateStatus>,
): boolean {
  const job = mockup?.backJob;
  if (!job || !backDoc?.mockup) return false;
  if (!templates.some((t) => t.template.id === job.templateId)) return false;
  return templateStatus[job.templateId]?.state === 'ready';
}

/** `Chest 12 × 16 in · 382 × 509 px · Sleeve 4 × 4 in · 120 × 120 px` — every area this blank
 *  offers, as the shop sizes it and as the photo holds it (the quad's bounding box, which is the
 *  area itself for the flat blanks and its envelope for a keystoned one). An area with no name of
 *  its own — a hand-built template that never went through `parseTemplate` — says only its size. */
export function printAreaLine(template: MockupTemplate): string {
  const n = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(1));
  return areasOf(template).map((area) => {
    const b = quadBounds(area.quad);
    const label = area.name ? `${area.name} ` : '';
    return `${label}${n(area.widthIn)} × ${n(area.heightIn)} in · ${b.x1 - b.x0} × ${b.y1 - b.y0} px`;
  }).join(' · ');
}

/**
 * Whether two images can stand in for each other on the canvas: same shape, whatever their sizes.
 *
 * The viewer draws BOTH the worker's CPU render and the GPU preview frame stretched to the photo's
 * full-resolution size, so the only thing that makes one a valid substitute for the other is their
 * aspect ratio. In the steady state they share it exactly (both come from the same template photo).
 * They do not for one render's worth of time after the job's template changes to one with a
 * DIFFERENT photo shape whose maps are already cached: the GPU has the new template's frame while the
 * canvas is still sized for the old template's render, and drawing one into the other would stretch
 * the garment until the new CPU render lands ~85 ms later. Failing this test is what sends the viewer
 * back to the CPU bitmap for that window.
 *
 * A ratio comparison rather than an equality of sizes, because the two are legitimately different
 * sizes: the CPU render reports FULL-RES dimensions and the GPU frame is the preview-size buffer.
 */
export function sameAspect(a: { width: number; height: number }, b: { width: number; height: number }, tolerance = 1e-3): boolean {
  if (!(a.width > 0) || !(a.height > 0) || !(b.width > 0) || !(b.height > 0)) return false;
  return Math.abs(a.width / a.height - b.width / b.height) < tolerance;
}
